import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const TOUR_STEPS = [
  {
    slug: 'advisor',
    copy: 'Si no sabes si algo que te están haciendo en el trabajo es legal, pregúntale aquí. Te responde basado en la Ley Federal del Trabajo, sin tecnicismos.',
  },
  {
    slug: 'job-evaluator',
    copy: '¿Te ofrecieron un trabajo? Compara salario y prestaciones aquí y sabe si la oferta es buena, regular o mala antes de firmar.',
  },
  {
    slug: 'severance',
    copy: 'Si te despidieron, calcula aquí cuánto te deben: indemnización, aguinaldo y vacaciones, en minutos.',
  },
  {
    slug: 'salary-benchmark',
    copy: 'Compara tu sueldo aquí con datos reales del mercado mexicano.',
  },
  {
    slug: 'net-salary',
    copy: 'Calcula aquí cuánto te va a llegar realmente después de impuestos.',
  },
  {
    slug: 'retiro',
    copy: 'Revisa aquí tus semanas cotizadas y qué puedes hacer para mejorar tu pensión.',
  },
];

const TOOLTIP_WIDTH = 320;
const SPOTLIGHT_PADDING = 8;

// Mismo mecanismo hecho a mano que LandingTour (ver ese archivo, ya borrado): sigue la
// posición de una tarjeta ya existente en el DOM con getBoundingClientRect, en vez de
// agregar una librería de tours solo para esto. Aquí apunta a data-testid="tool-<slug>"
// del Dashboard en vez de las tarjetas del landing.
export function DashboardTour({ active, onClose }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (active) setStepIndex(0);
  }, [active]);

  const updatePosition = useCallback(() => {
    const el = document.querySelector(`[data-testid="tool-${TOUR_STEPS[stepIndex].slug}"]`);
    if (!el) {
      setRect(null);
      return;
    }
    setRect(el.getBoundingClientRect());
  }, [stepIndex]);

  useEffect(() => {
    if (!active) return;
    const el = document.querySelector(`[data-testid="tool-${TOUR_STEPS[stepIndex].slug}"]`);
    if (el) {
      // Scroll instantáneo (sin behavior: 'smooth'): ver la nota original en LandingTour
      // sobre por qué animar el scroll es más frágil y más caro de repintar aquí.
      el.scrollIntoView({ block: 'center' });
    }
    updatePosition();

    let debounceHandle = null;
    const debouncedUpdate = () => {
      clearTimeout(debounceHandle);
      debounceHandle = setTimeout(updatePosition, 150);
    };
    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('scroll', debouncedUpdate, { passive: true });
    return () => {
      clearTimeout(debounceHandle);
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('scroll', debouncedUpdate);
    };
  }, [active, stepIndex, updatePosition]);

  if (!active || !rect) return null;

  const isLast = stepIndex === TOUR_STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      onClose();
      return;
    }
    setStepIndex((i) => Math.min(TOUR_STEPS.length - 1, i + 1));
  };
  const handlePrev = () => setStepIndex((i) => Math.max(0, i - 1));

  const spotlightStyle = {
    position: 'fixed',
    top: rect.top - SPOTLIGHT_PADDING,
    left: rect.left - SPOTLIGHT_PADDING,
    width: rect.width + SPOTLIGHT_PADDING * 2,
    height: rect.height + SPOTLIGHT_PADDING * 2,
    borderRadius: '16px',
    boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.75)',
    border: '2px solid #2563EB',
    zIndex: 9998,
    pointerEvents: 'none',
    transition: 'top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease',
  };

  const viewportBelow = window.innerHeight - rect.bottom;
  const placeAbove = viewportBelow < 220 && rect.top > 220;
  const tooltipWidth = Math.min(TOOLTIP_WIDTH, window.innerWidth - 32);
  const tooltipLeft = Math.min(Math.max(rect.left, 16), window.innerWidth - tooltipWidth - 16);

  const tooltipStyle = {
    position: 'fixed',
    left: tooltipLeft,
    top: placeAbove ? rect.top - SPOTLIGHT_PADDING - 12 : rect.bottom + SPOTLIGHT_PADDING + 12,
    transform: placeAbove ? 'translateY(-100%)' : 'none',
    width: tooltipWidth,
    zIndex: 9999,
  };

  return (
    <>
      <div style={spotlightStyle} data-testid="dashboard-tour-spotlight" />
      <div style={tooltipStyle} className="bg-white rounded-xl shadow-2xl border p-5" data-testid="dashboard-tour-tooltip">
        <div className="flex items-center gap-1.5 mb-3">
          {TOUR_STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === stepIndex ? 'w-6 bg-[#2563EB]' : 'w-1.5 bg-slate-200'}`}
            />
          ))}
        </div>
        <p className="text-sm text-[#0F172A] leading-relaxed" data-testid="dashboard-tour-copy">
          {TOUR_STEPS[stepIndex].copy}
        </p>
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-1">
            {stepIndex > 0 && (
              <Button variant="ghost" size="sm" onClick={handlePrev} data-testid="dashboard-tour-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="dashboard-tour-skip">
              Saltar
            </Button>
          </div>
          <Button
            size="sm"
            onClick={handleNext}
            className="bg-[#2563EB] hover:bg-[#2563EB]/90"
            data-testid="dashboard-tour-next"
          >
            {isLast ? 'Listo' : <>Siguiente <ChevronRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
      </div>
    </>
  );
}
